import { Link } from 'react-router-dom';
import { memo, ReactNode } from 'react';
import classes from 'classnames';
import ExternalIcon from 'assets/icons/external.svg?react';
import Loader from './Loader/Loader';
import Ripples from 'react-ripples';
import styles from './Button.module.scss';

export type Props = {
  type?: 'submit' | 'button';
  children?: ReactNode;
  outline?: boolean;
  disabled?: boolean;
  disableDefaultStyle?: boolean;
  className?: string;
  dataTest?: string;
  wide?: boolean;
  pending?: boolean;
  size?: 's' | 'm' | 'l';
  onClick?: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseEnter?: (
    event?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>,
  ) => void;
  /* When given, makes button act as link */
  to?: string;
  /* Used when `to` is passed. Determins whether to use inner router or link to external site */
  external?: boolean;
  buttonWrapperClassName?: string;
  buttonClassName?: string;
};

export default memo(function Button({
  children,
  type = 'button',
  outline = false,
  disabled = false,
  wide = false,
  pending = false,
  className,
  dataTest = 'button',
  size = 'l',
  onClick,
  onMouseEnter,
  to = '',
  external,
  buttonClassName,
}: Props) {
  const buttonStyle = classes(
    styles.button,
    outline && styles.outline,
    pending && styles.pending,
    styles[size],
    buttonClassName,
    wide && styles.wide,
    'fakeWebflowCnId',
  );

  return (
    <Ripples
      className={classes(
        styles.ripples,
        styles.container,
        styles[size],
        wide && styles.wide,
        className,
      )}
      color="rgba(255, 255, 255, 0.4)"
      placeholder={undefined}
      onPointerEnterCapture={undefined}
      onPointerLeaveCapture={undefined}
    >
      {to && !external && (
        <Link
          className={buttonStyle}
          onMouseEnter={onMouseEnter}
          data-test={dataTest}
          data-testid={dataTest}
          to={to}
        >
          {pending && (
            <div className={styles[`loader-wrapper-${size}`]}>
              <Loader outline={outline} />
            </div>
          )}
          {!(size === 's' && pending) && children}
        </Link>
      )}
      {to && external && (
        <a
          className={buttonStyle}
          onMouseEnter={onMouseEnter}
          data-test={dataTest}
          data-testid={dataTest}
          href={to}
          rel="noopener"
        >
          {pending && (
            <div className={styles[`loader-wrapper-${size}`]}>
              <Loader outline={outline} />
            </div>
          )}
          {!(size === 's' && pending) && children}
          {external && <ExternalIcon className={styles['external-icon']} />}
        </a>
      )}
      {!to && (
        <button
          data-test={dataTest}
          data-testid={dataTest}
          disabled={pending ? true : disabled}
          type={type}
          onClick={onClick}
          className={buttonStyle}
          onMouseEnter={onMouseEnter}
        >
          {pending && (
            <div className={styles[`loader-wrapper-${size}`]}>
              <Loader outline={outline} />
            </div>
          )}
          {!(size === 's' && pending) && children}
        </button>
      )}
    </Ripples>
  );
});
